import React, { useState, useEffect } from 'react';
import { UserLayout } from '../../components/user/UserLayout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { TrendingUp, Clock, Phone, CheckCircle, PhoneCall, Save, BarChart3 } from 'lucide-react';
import axios from 'axios';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const IndustryBenchmarks = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [benchmarks, setBenchmarks] = useState({
    totalAllocations: '',
    avgAllocationLength: '',
    totalCalls: '',
    avgCallsPerAllocation: '',
    avgTimePerCall: '',
    proactiveCheckins: '',
    avgProactivePerAllocation: '',
    onDemandCheckins: '',
    avgOnDemandPerAllocation: ''
  });
  const { toast } = useToast();

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      fetchBenchmarks(parsedUser.entityId);
    }
  }, []);

  const fetchBenchmarks = async (propertyId) => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/industry-benchmarks/${propertyId}`);
      if (response.data.success && response.data.benchmark) {
        const b = response.data.benchmark;
        setBenchmarks({
          totalAllocations: b.totalAllocations ?? '',
          avgAllocationLength: b.avgAllocationLength ?? '',
          totalCalls: b.totalCalls ?? '',
          avgCallsPerAllocation: b.avgCallsPerAllocation ?? '',
          avgTimePerCall: b.avgTimePerCall ?? '',
          proactiveCheckins: b.proactiveCheckins ?? '',
          avgProactivePerAllocation: b.avgProactivePerAllocation ?? '',
          onDemandCheckins: b.onDemandCheckins ?? '',
          avgOnDemandPerAllocation: b.avgOnDemandPerAllocation ?? ''
        });
      }
    } catch (error) {
      console.error('Error fetching benchmarks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setBenchmarks(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    if (!user) return;
    
    try {
      setSaving(true);
      
      // Convert empty strings to null, otherwise to float
      const payload = {
        propertyId: user.entityId,
        totalAllocations: benchmarks.totalAllocations !== '' ? parseFloat(benchmarks.totalAllocations) : null,
        avgAllocationLength: benchmarks.avgAllocationLength !== '' ? parseFloat(benchmarks.avgAllocationLength) : null,
        totalCalls: benchmarks.totalCalls !== '' ? parseFloat(benchmarks.totalCalls) : null,
        avgCallsPerAllocation: benchmarks.avgCallsPerAllocation !== '' ? parseFloat(benchmarks.avgCallsPerAllocation) : null,
        avgTimePerCall: benchmarks.avgTimePerCall !== '' ? parseFloat(benchmarks.avgTimePerCall) : null,
        proactiveCheckins: benchmarks.proactiveCheckins !== '' ? parseFloat(benchmarks.proactiveCheckins) : null,
        avgProactivePerAllocation: benchmarks.avgProactivePerAllocation !== '' ? parseFloat(benchmarks.avgProactivePerAllocation) : null,
        onDemandCheckins: benchmarks.onDemandCheckins !== '' ? parseFloat(benchmarks.onDemandCheckins) : null,
        avgOnDemandPerAllocation: benchmarks.avgOnDemandPerAllocation !== '' ? parseFloat(benchmarks.avgOnDemandPerAllocation) : null
      };
      
      const response = await axios.post(`${BACKEND_URL}/api/industry-benchmarks`, payload);
      
      if (response.data.success) {
        toast({
          title: "Success",
          description: "Industry benchmarks saved successfully",
        });
      }
    } catch (error) {
      console.error('Error saving benchmarks:', error);
      toast({
        title: "Error",
        description: "Failed to save benchmarks",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const metricFields = [
    {
      key: 'totalAllocations',
      label: 'Total Allocations',
      description: 'Average total allocations per day/period',
      icon: TrendingUp,
      unit: '',
      color: 'text-blue-600',
      bgColor: 'bg-blue-50'
    },
    {
      key: 'avgAllocationLength',
      label: 'Avg Allocation Length',
      description: 'Average duration of an allocation',
      icon: Clock,
      unit: 'minutes',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      key: 'totalCalls',
      label: 'Total Calls',
      description: 'Average total calls per day/period',
      icon: Phone,
      unit: '',
      color: 'text-purple-600',
      bgColor: 'bg-purple-50'
    },
    {
      key: 'avgCallsPerAllocation',
      label: 'Avg Calls Per Allocation',
      description: 'Average number of calls per allocation',
      icon: Phone,
      unit: '',
      color: 'text-orange-600',
      bgColor: 'bg-orange-50'
    },
    {
      key: 'avgTimePerCall',
      label: 'Avg Time Per Call',
      description: 'Average response time per call',
      icon: Clock,
      unit: 'seconds',
      color: 'text-teal-600',
      bgColor: 'bg-teal-50'
    },
    {
      key: 'proactiveCheckins',
      label: 'Total Proactive Checkins',
      description: 'Average proactive checkins (under 5 seconds)',
      icon: CheckCircle,
      unit: '',
      color: 'text-emerald-600',
      bgColor: 'bg-emerald-50'
    },
    {
      key: 'avgProactivePerAllocation',
      label: 'Avg Proactive Per Allocation',
      description: 'Average proactive checkins per allocation',
      icon: CheckCircle,
      unit: '',
      color: 'text-green-600',
      bgColor: 'bg-green-50'
    },
    {
      key: 'onDemandCheckins',
      label: 'Total On Demand Calls',
      description: 'Average on-demand calls (5+ seconds)',
      icon: PhoneCall,
      unit: '',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    },
    {
      key: 'avgOnDemandPerAllocation',
      label: 'Avg On Demand Per Allocation',
      description: 'Average on-demand calls per allocation',
      icon: PhoneCall,
      unit: '',
      color: 'text-amber-600',
      bgColor: 'bg-amber-50'
    }
  ];

  if (loading) {
    return (
      <UserLayout>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Loading benchmarks...</p>
          </div>
        </div>
      </UserLayout>
    );
  }

  return (
    <UserLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-800">Industry Benchmarks</h1>
              <p className="text-slate-600 mt-1">Set benchmark values to compare performance against industry standards</p>
            </div>
          </div>
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700"
          >
            {saving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Benchmarks
              </>
            )}
          </Button>
        </div>

        {/* Info Card */}
        <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <div className="flex items-start space-x-3">
            <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-800">How Benchmarks Work</h3>
              <p className="text-sm text-blue-600 mt-1">
                These values will be used in comparison reports to indicate whether actual metrics are above or below industry standards. 
                Leave a field empty if you don't want to set a benchmark for that metric.
              </p>
            </div>
          </div>
        </Card>

        {/* Benchmark Fields */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {metricFields.map((field) => {
            const Icon = field.icon;
            return (
              <Card key={field.key} className="p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start space-x-3 mb-4">
                  <div className={`w-10 h-10 ${field.bgColor} rounded-lg flex items-center justify-center`}>
                    <Icon className={`w-5 h-5 ${field.color}`} />
                  </div>
                  <div className="flex-1">
                    <Label className="text-sm font-semibold text-slate-700">{field.label}</Label>
                    <p className="text-xs text-slate-500 mt-0.5">{field.description}</p>
                  </div>
                </div>
                <div className="relative">
                  <Input
                    type="number"
                    step="0.01"
                    placeholder="Enter benchmark value"
                    value={benchmarks[field.key]}
                    onChange={(e) => handleChange(field.key, e.target.value)}
                    className="pr-16"
                  />
                  {field.unit && (
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-slate-400">
                      {field.unit}
                    </span>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      </div>
    </UserLayout>
  );
};
